import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { ForecastVersion } from '../../types';

interface EditVersionModalProps {
  isOpen: boolean;
  version: ForecastVersion | null;
  onClose: () => void;
  onSubmit: (params: EditVersionParams) => Promise<void>;
}

export interface EditVersionParams {
  name?: string;
  description?: string;
  year?: number;
}

const inputFocus = {
  onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--lodge-orange-400) 20%, transparent)';
    e.currentTarget.style.borderColor = 'var(--lodge-orange-400)';
  },
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.boxShadow = '';
    e.currentTarget.style.borderColor = '';
  },
};

export function EditVersionModal({ isOpen, version, onClose, onSubmit }: EditVersionModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Seed the form from the version each time the modal opens.
  useEffect(() => {
    if (isOpen && version) {
      setName(version.name);
      setDescription(version.description ?? '');
      setYear(version.year);
    }
  }, [isOpen, version]);

  if (!isOpen || !version) return null;

  const baseYear = version.year;
  const yearOptions = Array.from(new Set([baseYear - 2, baseYear - 1, baseYear, baseYear + 1, baseYear + 2, year])).sort();
  const yearChanged = year !== baseYear;
  const nameChanged = name.trim() !== version.name;
  const descriptionChanged = description !== (version.description ?? '');
  const dirty = nameChanged || descriptionChanged || yearChanged;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a version name');
      return;
    }
    if (yearChanged) {
      const ok = window.confirm(
        `Move "${version.name}" from fiscal year ${baseYear} to ${year}?\n\n` +
        `Every month-specific input in this version — overrides, daily values, room-stat overrides, ` +
        `adjustments and month locks — will be re-dated to ${year}. Driver settings carry over unchanged. ` +
        `Values will recalculate against ${year} actuals and on-the-books data.`
      );
      if (!ok) return;
    }
    setIsSubmitting(true);
    try {
      const params: EditVersionParams = {};
      if (nameChanged) params.name = name.trim();
      if (descriptionChanged) params.description = description;
      if (yearChanged) params.year = year;
      await onSubmit(params);
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to update version');
      console.error('Update version error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="fv3-scrim absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="fv3-dialog relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="fv3-dialog-head flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="fv3-dialog-title text-lg font-semibold text-slate-800">Edit Version</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Version Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
              autoFocus
              {...inputFocus}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none resize-none"
              {...inputFocus}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fiscal Year</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none"
              {...inputFocus}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {yearChanged && (
              <p className="text-xs mt-1.5" style={{ color: 'var(--lodge-orange-500)' }}>
                Month-specific inputs (overrides, daily values, adjustments, locks) will be re-dated from {baseYear} to {year}.
                Drivers carry over unchanged and values recalculate against {year} data.
              </p>
            )}
          </div>

          {version.type === 'Budget' && (
            <p className="text-xs text-slate-500">
              {version.budgetReferenceYear == null
                ? 'Saved Budget assignments keep their original same-year published budget basis. Select an LY Published Budget driver to use the prior year.'
                : `Budget reference: ${version.budgetReferenceYear + (year - baseYear)}. Budget-based rates use that year's amounts, room counts, and revenue shares.`}
              {' '}Saved monthly rate and value overrides take precedence. Copying a version leaves the original unchanged.
            </p>
          )}
          <div className="fv3-dialog-actions flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || !dirty}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', border: 'none', boxShadow: 'var(--shadow-orange)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
            >
              {isSubmitting ? (<><Loader2 size={16} className="animate-spin" />Saving...</>) : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditVersionModal;
